import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { AlertTriangle, HelpCircle, type LucideIcon } from 'lucide-react'
import { ReactNode, forwardRef, useEffect, useImperativeHandle, useState } from 'react'

type Props = {
    trigger?: ReactNode
    title: string
    message?: string
    primaryAction: (arg?: () => void) => void
    loading?: boolean
    onClose?: () => void
    open?: boolean
    setOpen?: (arg: boolean) => void
    children?: ReactNode
    className?: string
    variant?: 'default' | 'destructive'
    confirmLabel?: string
    cancelLabel?: string
    icon?: LucideIcon
}

export type ConfirmRefProps = {
    open: () => void
    close: () => void
    cancel: () => void
}

const variantConfig = {
    default: {
        icon: HelpCircle,
        iconBg: 'bg-slate-100',
        iconColor: 'text-slate-600',
        buttonVariant: 'default' as const,
    },
    destructive: {
        icon: AlertTriangle,
        iconBg: 'bg-red-50',
        iconColor: 'text-red-500',
        buttonVariant: 'destructive' as const,
    },
}

export const Confirm = forwardRef<ConfirmRefProps, Props>((props, ref) => {
    const {
        trigger,
        title,
        message,
        primaryAction,
        loading,
        onClose,
        open,
        setOpen,
        children,
        className,
        variant = 'default',
        confirmLabel = 'Confirm',
        cancelLabel = 'Cancel',
        icon,
    } = props

    const [openInternal, setOpenInternal] = useState(open || false)
    const hasExternalState = open !== undefined && setOpen !== undefined
    const isOpen = hasExternalState ? open : openInternal
    const setIsOpen = hasExternalState ? setOpen : setOpenInternal

    const config = variantConfig[variant]
    const Icon = icon || config.icon

    useEffect(() => {
        if (hasExternalState && open !== undefined) {
            setOpenInternal(open)
        }
    }, [open, hasExternalState])

    const handleCloseModal = () => {
        setIsOpen(false)
        onClose?.()
    }

    useImperativeHandle(ref, () => ({
        open: () => setIsOpen(true),
        close: () => handleCloseModal(),
        cancel: () => setIsOpen(false),
    }))

    return (
        <Dialog
            open={isOpen}
            onOpenChange={value => {
                if (!value) onClose?.()
                setIsOpen(value)
            }}
        >
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent className={cn('sm:max-w-[425px]', className)}>
                <DialogHeader className='flex-row items-start gap-4 space-y-0'>
                    <div className={cn('flex-shrink-0 p-2.5 rounded-full', config.iconBg)}>
                        <Icon className={cn('w-5 h-5', config.iconColor)} />
                    </div>
                    <div className='flex-1 space-y-1.5'>
                        <DialogTitle>{title}</DialogTitle>
                        {message && (
                            <DialogDescription className='text-slate-500'>{message}</DialogDescription>
                        )}
                    </div>
                </DialogHeader>

                {children && <div className='pl-[52px]'>{children}</div>}

                <DialogFooter>
                    <Button
                        variant='outline'
                        onClick={e => {
                            e.stopPropagation()
                            handleCloseModal()
                        }}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type='button'
                        variant={config.buttonVariant}
                        loading={loading}
                        onClick={e => {
                            e.stopPropagation()
                            primaryAction(() => setIsOpen(false))
                        }}
                    >
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
})
